/**
 * Content-derived React keys for sandboxed preview iframes.
 *
 * Every artifact surface that shows model-authored HTML renders it into an
 * `<iframe srcDoc={...}>`. Reusing one iframe element and mutating `srcdoc` is
 * not reliable: Chromium drops a srcdoc navigation that is superseded while an
 * earlier one is still in flight, so a frame can keep displaying the first
 * document it ever loaded even though the attribute now holds newer content.
 *
 * It bit the slide deck hardest, because a streaming create changes each
 * slide's srcdoc exactly once — placeholder → finished — right in that window.
 * The deck rendered as blank thumbnails and a stage stuck on "Generating…"
 * while the artifact sat complete and correct in the database.
 *
 * Keying an iframe by its content sidesteps the whole question: changed
 * content is a *new* element that loads its final srcdoc once, on mount.
 * Unchanged content keeps its key and is never remounted, so frames that are
 * already correct pay nothing.
 *
 * @module lib/artifacts/frame-key
 */

/**
 * Cheap, stable 32-bit hash of a document body.
 *
 * Not cryptographic and not collision-proof — it only has to change when the
 * content changes. A collision would reuse an iframe that should have
 * remounted, which is the pre-existing behaviour, never something worse.
 *
 * @param content - The HTML that will become the frame's `srcDoc`.
 * @returns A short signed-int string suitable for composing into a React key.
 */
export function hashFrameContent(content: string): string {
  let hash = 0;
  for (let i = 0; i < content.length; i++) {
    hash = (Math.imul(31, hash) + content.charCodeAt(i)) | 0;
  }
  return String(hash);
}
